import { useEffect, useRef } from "react";

import { capture } from "@/lib/posthog";
import { Network } from "vis-network/standalone";
import type { CompanyNetwork, CompanyNetworkContract } from "@/types/domain";

interface Socio {
  nombre: string;
  porcentaje: number;
}

// Nodo de proyecto. Bloqueado (gating de vistas) va en gris y sin nomenclatura:
// el canvas no admite blur, así que no se finge data — se dice que está
// bloqueado y el click abre el desbloqueo.
function projectNode(contract: CompanyNetworkContract) {
  const locked = contract.isLocked === true;
  return {
    id: `proj_${contract.contracting_id}`,
    label: locked
      ? "🔒 Licitación bloqueada"
      : wrapLabel(contract.nomenclature ?? "", 20),
    shape: "dot",
    size: 11,
    color: locked
      ? {
          background: "#94a3b8",
          border: "#64748b",
          highlight: { background: "#b6c2d1", border: "#64748b" },
        }
      : {
          background: "#2f80ed",
          border: "#1a5fb5",
          highlight: { background: "#4a90e2", border: "#1a5fb5" },
        },
    font: { color: "#000000", size: 11 },
    title: locked
      ? `${contract.entity ? `${contract.entity} · ` : ""}Click para desbloquear · 1 vista`
      : `${contract.entity || contract.nomenclature} · Click para ver detalle`,
  };
}

/** Soft-wrap a long label onto up to 3 lines, mirroring the Angular helper. */
function wrapLabel(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if ((current + " " + word).trim().length > maxLen) {
      if (current) lines.push(current.trim());
      current = word;
    } else {
      current = (current + " " + word).trim();
    }
  }
  if (current) lines.push(current.trim());
  return lines.slice(0, 3).join("\n");
}

export function CompanyNetworkGraph({
  companyId,
  companyName,
  network,
  gerente,
  socios,
  onOpenContract,
  onOpenCompany,
}: {
  companyId: number;
  companyName: string;
  network: CompanyNetwork;
  gerente: string;
  socios: Socio[];
  onOpenContract: (contractingId: number, locked: boolean) => void;
  onOpenCompany: (companyId: number) => void;
}) {
  useEffect(() => {
    capture("company_network_opened", { company_id: companyId });
  }, [companyId]);

  const containerRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<Network | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const BLACK_FONT = { color: "#000000", size: 11 };
    const FONT_BOLD = { color: "#000000", size: 13, bold: true } as const;

    const nodes: any[] = [];
    const edges: any[] = [];
    const seenCompanies = new Set<number>();
    const seenContracts = new Set<number>();
    const lockedContracts = new Set(
      [
        ...(network.directContracts ?? []),
        ...(network.consortiums ?? []).flatMap((c) => c.contracts ?? []),
      ]
        .filter((ct) => ct.isLocked)
        .map((ct) => ct.contracting_id),
    );

    const focusId = `focus_${companyId}`;

    // ── Foco (esta empresa) ──────────────────────────────────────────────
    nodes.push({
      id: focusId,
      label: wrapLabel(companyName || "Esta empresa", 22),
      shape: "dot",
      size: 24,
      color: {
        background: "#b07b1e",
        border: "#8a5e15",
        highlight: { background: "#c8901f", border: "#8a5e15" },
      },
      font: FONT_BOLD,
    });

    // ── Contactos (personas OSCE) ────────────────────────────────────────
    if (gerente) {
      const gid = "person_gerente";
      nodes.push({
        id: gid,
        label: wrapLabel(gerente, 20),
        shape: "dot",
        size: 10,
        color: {
          background: "#2e7d6b",
          border: "#1f5c4e",
          highlight: { background: "#3a9980", border: "#1f5c4e" },
        },
        font: BLACK_FONT,
        title: "Gerente General",
      });
      edges.push({
        from: focusId,
        to: gid,
        color: { color: "#a8d5cc" },
        width: 1,
        dashes: true,
      });
    }

    for (const socio of socios) {
      const sid = `person_${socio.nombre.replace(/\s/g, "_")}`;
      nodes.push({
        id: sid,
        label: wrapLabel(socio.nombre, 20),
        shape: "dot",
        size: 9,
        color: {
          background: "#2e7d6b",
          border: "#1f5c4e",
          highlight: { background: "#3a9980", border: "#1f5c4e" },
        },
        font: BLACK_FONT,
        title: `Accionista ${socio.porcentaje}%`,
      });
      edges.push({
        from: focusId,
        to: sid,
        color: { color: "#a8d5cc" },
        width: 1,
        dashes: true,
      });
    }

    // ── Proyectos directos ───────────────────────────────────────────────
    for (const ct of network.directContracts ?? []) {
      if (seenContracts.has(ct.contracting_id)) continue;
      seenContracts.add(ct.contracting_id);
      const node = projectNode(ct);
      nodes.push(node);
      edges.push({
        from: focusId,
        to: node.id,
        color: { color: "#93c5fd" },
        width: 1.5,
      });
    }

    // ── Consorcios → proyectos + empresas socias ─────────────────────────
    for (const consortium of network.consortiums ?? []) {
      for (const ct of consortium.contracts ?? []) {
        if (!seenContracts.has(ct.contracting_id)) {
          seenContracts.add(ct.contracting_id);
          nodes.push(projectNode(ct));
          edges.push({
            from: focusId,
            to: `proj_${ct.contracting_id}`,
            color: { color: "#93c5fd" },
            width: 1.5,
          });
        }
      }

      for (const partner of consortium.members ?? []) {
        const eid = `empresa_${partner.company_id}`;
        if (!seenCompanies.has(partner.company_id)) {
          seenCompanies.add(partner.company_id);
          // Cada socio con su propio candado: bloqueado va en gris y sin nombre.
          nodes.push({
            id: eid,
            label: partner.isLocked ? "Socio bloqueado" : wrapLabel(partner.company_name ?? "", 20),
            shape: "dot",
            size: 10,
            color: partner.isLocked
              ? {
                  background: "#94a3b8",
                  border: "#64748b",
                  highlight: { background: "#a8b4c4", border: "#64748b" },
                }
              : {
                  background: "#0f2742",
                  border: "#07182e",
                  highlight: { background: "#1a3d61", border: "#07182e" },
                },
            font: { color: "#000000", size: 10 },
            title: partner.isLocked
              ? "Desbloquéala para ver quién es · Click para ver detalle"
              : `${partner.company_ruc} · Click para ver detalle`,
          });
        }
        for (const ct of consortium.contracts ?? []) {
          edges.push({
            from: eid,
            to: `proj_${ct.contracting_id}`,
            color: { color: "#94a3b8" },
            width: 1,
            smooth: { enabled: true, type: "dynamic", roundness: 0.3 },
          });
        }
      }
    }

    const options = {
      physics: {
        enabled: true,
        barnesHut: {
          gravitationalConstant: -5000,
          springLength: 160,
          springConstant: 0.03,
          damping: 0.25,
        },
        stabilization: { iterations: 250 },
      },
      interaction: { hover: true, tooltipDelay: 80 },
      edges: { arrows: { to: { enabled: false } } },
      nodes: { borderWidth: 2 },
    };

    const net = new Network(
      containerRef.current,
      { nodes, edges },
      options,
    );
    networkRef.current = net;

    net.on("click", (params) => {
      const nodeId: string | undefined = params.nodes?.[0] as string;
      if (!nodeId) return;
      if (nodeId.startsWith("empresa_")) {
        onOpenCompany(Number(nodeId.replace("empresa_", "")));
      } else if (nodeId.startsWith("proj_")) {
        const contractingId = Number(nodeId.replace("proj_", ""));
        onOpenContract(contractingId, lockedContracts.has(contractingId));
      }
    });

    net.on("hoverNode", (params) => {
      const nodeId: string = params.node;
      if (
        containerRef.current &&
        (nodeId.startsWith("empresa_") || nodeId.startsWith("proj_"))
      ) {
        containerRef.current.style.cursor = "pointer";
      }
    });
    net.on("blurNode", () => {
      if (containerRef.current) containerRef.current.style.cursor = "default";
    });

    return () => {
      net.destroy();
      networkRef.current = null;
    };
  }, [
    companyId,
    companyName,
    network,
    gerente,
    socios,
    onOpenContract,
    onOpenCompany,
  ]);

  return (
    <div
      ref={containerRef}
      className="h-[380px] w-full overflow-hidden rounded-xl border border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50"
    />
  );
}
